import { ErrorObject } from "ajv";

export function formatAjvError(err: ErrorObject): string {
  let detail = "";

  switch (err.keyword) {
    case "maxItems":
    case "minItems":
    case "maxLength":
    case "minLength":
    case "maxProperties":
    case "minProperties":
      detail = `Limit: ${String((err.params as { limit?: unknown }).limit)}`;
      break;
    case "additionalItems":
      detail = `Limit: ${String((err.params as { limit?: unknown }).limit)}`;
      break;
    case "additionalProperties":
      detail = `Unexpected property: ${String((err.params as { additionalProperty?: unknown }).additionalProperty)}`;
      break;
    case "dependencies":
      detail = `Missing dependency: ${String((err.params as { missingProperty?: unknown }).missingProperty)} (requires: ${String((err.params as { deps?: unknown }).deps)})`;
      break;
    case "format":
      detail = `Format: ${String((err.params as { format?: unknown }).format)}`;
      break;
    case "maximum":
    case "minimum":
    case "exclusiveMaximum":
    case "exclusiveMinimum":
      detail = `Limit: ${String((err.params as { limit?: unknown }).limit)} (${String((err.params as { comparison?: unknown }).comparison)})`;
      break;
    case "multipleOf":
      detail = `Multiple of: ${String((err.params as { multipleOf?: unknown }).multipleOf)}`;
      break;
    case "pattern":
      detail = `Pattern: ${String((err.params as { pattern?: unknown }).pattern)}`;
      break;
    case "required":
      detail = `Missing property: ${String((err.params as { missingProperty?: unknown }).missingProperty)}`;
      break;
    case "propertyNames":
      detail = `Invalid property name: ${String((err.params as { propertyName?: unknown }).propertyName)}`;
      break;
    case "enum":
      if ((err.params as { allowedValues?: unknown[] }).allowedValues) {
        detail = `Allowed: ${(err.params as { allowedValues: unknown[] }).allowedValues.join(", ")}`;
      }
      break;
  }

  return `${err.message}${detail ? ` (${detail})` : ""}`;
}

export function collectAjvErrorDetails(
  errors: ErrorObject[] | null | undefined,
): string[] {
  if (!errors?.length) {
    return [];
  }

  const grouped: Record<string, ErrorObject[]> = {};

  errors.forEach((err) => {
    const path = err.instancePath || "Root";

    if (!grouped[path]) {
      grouped[path] = [];
    }

    grouped[path].push(err);
  });

  const details: string[] = [];

  Object.entries(grouped).forEach(([path, pathErrors]) => {
    const messages: string[] = [];
    const typeErrors = pathErrors.filter((error) => error.keyword === "type");
    const requiredErrors = pathErrors.filter((error) => error.keyword === "required");
    const otherErrors = pathErrors.filter(
      (error) => !["type", "required", "oneOf", "anyOf", "if"].includes(error.keyword),
    );
    const combinerErrors = pathErrors.filter((error) =>
      ["oneOf", "anyOf", "if"].includes(error.keyword),
    );

    if (typeErrors.length > 0) {
      const types = typeErrors.map(
        (error) =>
          (error.params as { type?: string }).type
          || error.message?.replace("must be ", ""),
      );
      const uniqueTypes = types.filter((value, index, values) => values.indexOf(value) === index);
      messages.push(`can be ${uniqueTypes.join(", ")}, but is not`);
    }

    if (requiredErrors.length > 0) {
      const missingProps = requiredErrors.map(
        (error) => String((error.params as { missingProperty?: unknown }).missingProperty),
      );
      messages.push(
        `if object then must have '${missingProps.join("', '")}' properties`,
      );
    }

    otherErrors.forEach((error) => {
      messages.push(formatAjvError(error));
    });

    if (
      typeErrors.length === 0
      && requiredErrors.length === 0
      && otherErrors.length === 0
    ) {
      combinerErrors.forEach((error) => {
        messages.push(formatAjvError(error));
      });
    }

    if (messages.length > 0) {
      const uniqueMessages = messages.filter(
        (message, index) => messages.indexOf(message) === index,
      );
      details.push(`${path}:`);
      uniqueMessages.forEach((message) => details.push(`  - ${message}`));
    }
  });

  return details;
}
